import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { CONSTANTS } from "common";
import useSnackbar from "./useSnackbar";
import useUser from "./useUser";
// import { getTranslation } from "common";
// import useTranslations from "./useTranslations";

const useSSE = <T>(
  url: string,
  callback: (data: T) => void,
  needJWT: boolean = false
) => {
  const [mounted, setMounted] = useState<boolean>(false);
  const user = useUser();
  const { sendSnack } = useSnackbar();
  const lastEventIdRef = useRef<number>(-1);
  // const translations = useTranslations();

  // const _onError = useCallback(
  //   (message: Event) => {
  //     sendSnack({
  //       message: getTranslation(translations, "generic.server.error"),
  //       type: "error",
  //     });
  //   },
  //   [translations, sendSnack]
  // );

  // Il callback va letto da una ref, non catturato direttamente: `_onOpen` e'
  // memoizzato e legava `listener.onmessage` al callback del PRIMO render.
  // Quello chiudeva su un `reload` che a quel punto vedeva `organization` ancora
  // nullo (viene da un context), quindi faceva `if (!organization?.id) return`:
  // ogni evento SSE diventava un no-op e il task appena spostato restava in
  // `loading` finche' non si ricaricava la pagina a mano.
  const callbackRef = useRef(callback);
  const previousUrlRef = useRef(url);
  useLayoutEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  const _callback = useCallback(
    (message: MessageEvent<string>) => {
      try {
        message.stopPropagation();
        // lastEventId.current = Number(message.lastEventId);
        lastEventIdRef.current = Number(message.lastEventId);
        if (message.data) {
          callbackRef.current(JSON.parse(message.data) as T);
        }
      } catch (e) {
        sendSnack({
          message: String(e),
          type: "error",
          autoClose: 5000,
        });
      }
    },
    [sendSnack]
  );
  const _onOpen = useCallback((ev: Event) => {
    const listener = ev.currentTarget as EventSource;
    // listener.onerror = _onError;
    listener.onmessage = _callback;
  }, [_callback]);
  const retryTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useLayoutEffect(() => setMounted(true), []);
  useEffect(() => {
    if (previousUrlRef.current !== url) {
      previousUrlRef.current = url;
      lastEventIdRef.current = -1;
    }

    const jwt = needJWT
      ? localStorage.getItem(CONSTANTS.STORE_JWT) || ""
      : "";
    const sseReady =
      Boolean(url) &&
      !url.includes("undefined") &&
      Boolean(process.env.REACT_APP_BACKEND_BASE_URI) &&
      mounted &&
      (!needJWT || (Boolean(user?.userId) && Boolean(jwt)));

    if (!sseReady) {
      return;
    }

    let listener: EventSource | null = null;

    const connect = () => {
      if (listener) {
        listener.close();
      }
      // Auth solo via cookie httpOnly (withCredentials): EventSource non puo'
      // mandare header Authorization, e ResolveFlowentiAuthToken legge il cookie
      // prima di qualunque query param. Il token non va piu' nell'URL: finiva
      // nei log del server con mesi di validita' residua.
      const params = new URLSearchParams({
        lasteventid: String(lastEventIdRef.current),
      });
      listener = new EventSource(
        `${process.env.REACT_APP_BACKEND_BASE_URI}${url}?${params.toString()}`,
        { withCredentials: true }
      );
      listener.addEventListener("open", _onOpen);
      listener.onerror = () => {
        listener?.close();
        if (retryTimer.current) {
          clearTimeout(retryTimer.current);
        }
        retryTimer.current = setTimeout(() => {
          retryTimer.current = null;
          connect();
        }, 3000);
      };
    };

    connect();

    return () => {
      if (retryTimer.current) {
        clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
      if (listener) {
        listener.removeEventListener("open", _onOpen);
        listener.onmessage = null;
        listener.onerror = null;
        listener.close();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // _callback,
    needJWT,
    // type,
    url,
    user?.userId,
    // _onError,
    mounted,
  ]);

  const resetLastEventID = useCallback(() => {
    lastEventIdRef.current = -1;
  }, []);

  return {
    resetLastEventID,
    mounted,
  };
};

export default useSSE;
